/**
 * Modal "Cambiar horario" (008 · T044 · US1; contracts/ui.md §1.3, FR-002).
 *
 * Crea una **versión nueva** de la plantilla (`POST /shift-templates/{id}/versions`) con "Vigente
 * desde" (por defecto mañana en la zona de la empresa) y el aviso "Los días anteriores se siguen
 * evaluando con el horario anterior". Arranca con las horas de la versión vigente. Una vigencia
 * dentro de un período cerrado vuelve como `409 PERIOD_CLOSED` sobre el campo de la fecha.
 */
import { Info } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { CreateShiftTemplateVersionBodySchema, type ShiftTemplate } from '@fermin/contracts';

import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { useToast } from '../../../components/ui/toast';
import { t } from '../../../i18n';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import { scheduleErrorMessage, useCreateTemplateVersion } from '../api';
import { addDays, todayInZone } from '../format';

import {
  applyApiFieldError,
  applyIssues,
  SHIFT_TIME_FIELDS,
  ShiftTimesFields,
  shiftTimesFromValues,
  shiftTimesValues,
  type ShiftTimesValues,
} from './template-form-dialog';

interface VersionFormValues extends ShiftTimesValues, Record<string, unknown> {
  effective_from: string;
}

const VERSION_FIELDS = [...SHIFT_TIME_FIELDS, 'effective_from'] as const;

/** Horas de la versión vigente y vigencia desde mañana (zona de la empresa). */
function initialValues(template: ShiftTemplate, companyTimezone: string): VersionFormValues {
  return {
    ...shiftTimesValues(template.current),
    effective_from: addDays(todayInZone(companyTimezone), 1),
  };
}

export interface TemplateVersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: ShiftTemplate;
  /** Zona de la empresa: "mañana" se calcula en ella. */
  companyTimezone: string;
  companyLateTolerance: number | null;
  companyEarlyTolerance: number | null;
}

export function TemplateVersionDialog({
  open,
  onOpenChange,
  template,
  companyTimezone,
  companyLateTolerance,
  companyEarlyTolerance,
}: TemplateVersionDialogProps) {
  const { toast } = useToast();
  const { readOnly } = useReadOnly();
  const create = useCreateTemplateVersion();

  const form = useForm<VersionFormValues>({
    defaultValues: initialValues(template, companyTimezone),
  });
  const { register, handleSubmit, reset, formState } = form;

  useEffect(() => {
    if (open) {
      reset(initialValues(template, companyTimezone));
    }
  }, [open, template, companyTimezone, reset]);

  const onSubmit = handleSubmit((values) => {
    form.clearErrors();
    const parsed = CreateShiftTemplateVersionBodySchema.safeParse({
      ...shiftTimesFromValues(values),
      effective_from: values.effective_from,
    });
    if (!parsed.success) {
      applyIssues(form, parsed.error.issues, VERSION_FIELDS);
      return;
    }
    create.mutate(
      { id: template.id, body: parsed.data },
      {
        onSuccess: (saved) => {
          toast({
            title: t('schedules.templates.toast.versioned', {
              version: saved.versions[0]?.version ?? '',
            }),
            variant: 'success',
          });
          onOpenChange(false);
        },
        onError: (error) => {
          if (!applyApiFieldError(form, error, VERSION_FIELDS)) {
            toast({ title: scheduleErrorMessage(error), variant: 'error' });
          }
        },
      },
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t('schedules.templates.version.title', { name: template.name })}
        description={t('schedules.templates.version.description')}
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
              {t('common.buttons.cancel')}
            </Button>
            <Button
              type="submit"
              form="shift-template-version-form"
              loading={create.isPending}
              loadingText={t('common.states.saving')}
              {...readOnlyProps(readOnly)}
            >
              {t('schedules.templates.version.submit')}
            </Button>
          </>
        }
      >
        <form
          id="shift-template-version-form"
          data-testid="shift-template-version-form"
          className="flex flex-col gap-fm-4"
          onSubmit={(event) => void onSubmit(event)}
          noValidate
        >
          <ShiftTimesFields
            form={form}
            companyLateTolerance={companyLateTolerance}
            companyEarlyTolerance={companyEarlyTolerance}
          />
          <Field
            label={t('schedules.templates.fields.effectiveFrom')}
            required
            error={formState.errors.effective_from?.message}
          >
            {(controlProps) => (
              <Input {...controlProps} type="date" {...register('effective_from')} />
            )}
          </Field>
          <p
            className="flex items-start gap-fm-2 rounded-fm-md bg-fm-info-soft p-fm-3 text-fm-sm text-fm-info-text"
            role="note"
          >
            <Info size={16} aria-hidden="true" className="mt-px shrink-0" />
            {t('schedules.templates.version.notice')}
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
